import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import classnames from 'classnames';
import regexpEscape from 'escape-string-regexp';
import { forwardRef, Fragment, useCallback, useEffect, useId, useImperativeHandle, useMemo, useRef, useState, } from 'react';
import { Button } from './button.js';
import { matches } from './matches.js';
export const MultiSelect = forwardRef(function MultiSelect(props, ref) {
    const { id = undefined, choices: choicesProp = [], choicesName, className: classNameProp, newName, disabled, groupLabel = false, groups, name, onChange, onOpen, onClose, placeholder = '', defaultValue = null, select = false, required = false, small = false, value: valueProp, limit, email = false, nestedLabel = '', ...rest } = props;
    const inputRef = useRef(null);
    const buttonRef = useRef(null);
    const requiredInputRef = useRef(null);
    const highlightedRef = useRef(null);
    const removeRef = useRef(null);
    // We need to allow form submit when pressing Enter, but preventDefault when
    // clicking the button. To differentiate between those events, we watch for
    // mousedown and keydown to decide if a click event was triggered by pressing
    // enter.
    const ignoreClick = useRef(false);
    const uid = useId();
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const focus = () => {
        const element = inputRef.current || buttonRef.current;
        element?.focus();
    };
    const open = () => {
        if (!disabled)
            setIsOpen(true);
    };
    const chooseValues = (options) => {
        for (const option of options || []) {
            choose(option);
        }
    };
    const chooseNewValues = (options) => {
        for (const option of options || []) {
            chooseNew(option);
        }
    };
    useImperativeHandle(ref, () => ({
        focus,
        open,
        chooseValues,
        chooseNewValues,
        clearAll,
    }));
    // If choices are grouped, flatten them into one list
    const choices = useMemo(() => {
        if (!groups) {
            if (!choicesProp)
                throw new Error('must provide groups or choices');
            return choicesProp;
        }
        return groups.flatMap(({ choices, name }, id) => {
            return choices.map((choice) => {
                const group = { id, name };
                return { ...choice, group };
            });
        });
    }, [choicesProp, groups]);
    const choicesIncludesValue = useCallback((value) => {
        for (const choice of choices) {
            if (value.toString().toLowerCase() ==
                choice.value.toString().toLowerCase() ||
                value.toString().toLowerCase() ==
                    choice.text.toString().toLowerCase()) {
                return true;
            }
        }
        return false;
    }, [choices]);
    // Value prop takes precedence over defaultValue
    const startingValues = 'value' in props ? valueProp : defaultValue;
    const defaultValues = startingValues?.filter((val) => choicesIncludesValue(val)) || [];
    const defaultNewValues = startingValues?.filter((val) => !choicesIncludesValue(val)) || [];
    const defaultDisplayValues = [...defaultValues, ...defaultNewValues];
    const [valueState, setValues] = useState(defaultValues);
    const values = 'value' in props ? defaultValues : valueState;
    const [newValues, setNewValues] = useState(defaultNewValues);
    const [displayValuesState, setDisplayValues] = useState(defaultDisplayValues);
    const displayValues = 'value' in props ? valueProp : displayValuesState;
    const filterDisplayValues = useCallback(() => {
        return displayValues?.filter((val) => {
            if ((values?.includes(val) || newValues?.includes(val)) &&
                (choicesIncludesValue(val) || newName)) {
                return true;
            }
            return false;
        });
    }, [displayValues, values, newValues, newName, choicesIncludesValue]);
    const choiceIds = useMemo(() => {
        return new Map(choices.map((choice, index) => [choice, index]));
    }, [choices]);
    const selectedValues = useMemo(() => {
        return values ? values : [];
    }, [values]);
    const selectedChoices = useMemo(() => {
        return choices.filter((choice) => {
            return selectedValues?.includes(choice.value);
        });
    }, [choices, selectedValues]);
    const newChoices = useMemo(() => {
        return newValues.map((value) => {
            return { text: value, value: value };
        });
    }, [newValues]);
    const displayChoices = useMemo(() => {
        return filterDisplayValues()?.map((value) => {
            if (choicesIncludesValue(value)) {
                return choices.find((choice) => {
                    return choice.value === value;
                });
            }
            else {
                return { text: value, value };
            }
        });
    }, [choices, choicesIncludesValue, filterDisplayValues]);
    const pattern = useMemo(() => {
        return query ? new RegExp(regexpEscape(query), 'ig') : null;
    }, [query]);
    const filtered = useMemo(() => {
        const choicesToFilter = choices.filter((choice) => {
            return !selectedChoices.includes(choice);
        });
        if (!pattern)
            return choicesToFilter;
        return choicesToFilter.filter(({ group, text }) => {
            pattern.lastIndex = 0;
            if (groups) {
                return pattern.test(text) || (group && pattern.test(group.name));
            }
            else {
                return pattern.test(text);
            }
        });
    }, [choices, groups, pattern, selectedChoices]);
    const limited = useMemo(() => {
        const choicesNameCopy = choicesName ? ' new ' + choicesName : '';
        const queryChoice = {
            text: 'Add' + choicesNameCopy + ' “' + query + '“',
            value: query,
        };
        const isAbleToAddNew = query.length &&
            newName &&
            !choicesIncludesValue(query) &&
            !newValues.includes(query);
        if (limit == null) {
            return isAbleToAddNew ? [...filtered, queryChoice] : filtered;
        }
        else {
            return isAbleToAddNew
                ? [...filtered.slice(0, limit), queryChoice]
                : [...filtered.slice(0, limit)];
        }
    }, [
        limit,
        filtered,
        query,
        choicesIncludesValue,
        newValues,
        newName,
        choicesName,
    ]);
    // Notify listeners
    useEffect(() => {
        if (isOpen) {
            onOpen?.();
        }
        else {
            onClose?.();
        }
    }, [isOpen, onClose, onOpen]);
    // Set focus and set highlighted index when opened
    useEffect(() => {
        if (!isOpen)
            return;
        setHighlightedIndex(0);
        focus();
    }, [limited, isOpen]);
    // The button is first rendered with tabindex -1 to prevent it from being
    // focused when pressing shift-tab. Once focus has changed, we can remove the
    // attribute.
    useEffect(() => {
        if (isOpen) {
            buttonRef.current?.setAttribute('aria-hidden', 'true');
            buttonRef.current?.setAttribute('tabindex', '-1');
        }
        else {
            setTimeout(() => {
                buttonRef.current?.removeAttribute('aria-hidden');
                buttonRef.current?.removeAttribute('tabindex');
            }, 0);
        }
    }, [isOpen]);
    const move = (n) => {
        setHighlightedIndex(Math.min(limited.length - 1, Math.max(0, highlightedIndex + n)));
    };
    const chooseNew = useCallback((newValue) => {
        newValue = typeof newValue == 'string' ? newValue.trim() : newValue;
        if (!newValues.includes(newValue) && !choicesIncludesValue(newValue)) {
            setDisplayValues([...(displayValues || []), newValue]);
            setNewValues([...newValues, newValue]);
            if (onChange)
                onChange({
                    values: selectedValues,
                    newValues: [...newValues, newValue],
                });
        }
        setQuery('');
    }, [
        choicesIncludesValue,
        newValues,
        onChange,
        selectedValues,
        displayValues,
    ]);
    const choose = useCallback((newValue) => {
        if (!selectedValues.includes(newValue)) {
            const existingChoice = choices.filter((choice) => {
                return choice.value == newValue;
            });
            if (existingChoice.length) {
                setDisplayValues([
                    ...displayValues,
                    existingChoice[0].value,
                ]);
                const orderedVals = [];
                choices.forEach((choice) => {
                    if (selectedValues.includes(choice.value) ||
                        newValue == choice.value) {
                        orderedVals.push(choice.value);
                    }
                });
                const selectedVals = [...orderedVals];
                setValues(selectedVals);
                if (onChange) {
                    onChange({ values: selectedVals, newValues: newValues });
                }
            }
            else {
                if (!inputRef.current?.reportValidity())
                    return;
                chooseNew(newValue);
            }
        }
        setQuery('');
    }, [choices, chooseNew, displayValues, onChange, newValues, selectedValues]);
    const close = useCallback(() => {
        if (!inputRef.current?.reportValidity())
            return;
        if (query && newName)
            choose(query);
        setIsOpen(false);
        setQuery('');
        setHighlightedIndex(-1);
    }, [choose, query, newName]);
    // The dropdown is never open when disabled
    useEffect(() => {
        if (disabled)
            close();
    }, [disabled, close]);
    const onChangeQuery = ({ target }) => {
        open();
        setQuery(target.value);
        setHighlightedIndex(0);
    };
    const removeValue = (index) => {
        if (displayValues?.length) {
            const remValue = displayValues[index];
            const remainingDisplayValues = displayValues.filter((value) => value !== remValue);
            setDisplayValues(remainingDisplayValues);
            let remainingValues = [];
            let remainingNewValues = [];
            if (selectedValues) {
                remainingValues = selectedValues.filter((value) => value !== remValue);
                setValues(remainingValues);
            }
            if (newValues) {
                remainingNewValues = newValues.filter((value) => value !== remValue);
                setNewValues(remainingNewValues);
            }
            if (onChange) {
                onChange({ values: remainingValues, newValues: remainingNewValues });
            }
        }
        else {
            open();
        }
    };
    const clearAll = () => {
        setValues([]);
        setNewValues([]);
        setDisplayValues([]);
        onChange?.({ values: [], newValues: [] });
    };
    const onButtonKeyDown = (event) => {
        // Skip when already open, default prevented, or meta key pressed.
        if (isOpen || event.defaultPrevented || event.metaKey)
            return;
        if (event.key === 'Escape') {
            event.stopPropagation();
            setTimeout(focus, 0);
            close();
        }
        // ArrowUp and ArrowDown opens the dropdown
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            open();
        }
        // Normal characters open the multi-select and populate the query
        if (event.key.length === 1) {
            open();
            setQuery(event.key);
            // Make sure the event doesn't bubble up and add the character twice.
            // https://github.com/harvesthq/harvestapp/issues/27462
            event.preventDefault();
        }
    };
    const onButtonClick = (event) => {
        // Submit for keyboard clicks, but not mouse clicks.
        if (ignoreClick.current) {
            event.preventDefault();
        }
        // When pressing enter in a separate form field, the first button element
        // in the form will receive a click event. If the default is prevented then
        // the form will not submit. As a result, we should make sure to not ignore
        // subsequent clicks.
        ignoreClick.current = false;
    };
    const onButtonMouseDown = (event) => {
        // Don't respond to right-click or removal button events
        if (event.button === 2 || event.target == removeRef.current)
            return;
        // Prevent default so that we don't steal focus back from the search input
        event.preventDefault();
        ignoreClick.current = true;
        open();
    };
    const onKeyUp = (event) => {
        switch (event.key) {
            case ' ':
                event.preventDefault();
        }
    };
    const onKeyDown = (event) => {
        const item = limited[highlightedIndex];
        switch (event.key) {
            case 'Tab':
                close();
                break;
            case 'Escape':
                // When the dropdown is open, pressing Escape should close it but not
                // cause a dialog to close.
                event.stopPropagation();
                setTimeout(focus, 0);
                close();
                break;
            case 'ArrowUp':
            case 'ArrowDown':
                event.preventDefault();
                move(event.key === 'ArrowUp' ? -1 : 1);
                break;
            case 'Enter':
                if (event.key === 'Enter') {
                    event.preventDefault();
                    setTimeout(focus, 0);
                }
                if (item) {
                    choose(item.value);
                }
                // We unset required to hide the validation bubble, but if the user
                // submits the form by pressing enter, we want to set it again before
                // the submit event.
                if (requiredInputRef.current) {
                    requiredInputRef.current.required = true;
                }
                break;
            case 'Backspace':
            case 'Delete':
                if (query.length === 0) {
                    event.preventDefault();
                    setTimeout(focus, 0);
                    if (displayValues) {
                        removeValue(displayValues.length - 1);
                    }
                }
        }
    };
    const renderSelections = () => {
        return (_jsxs("div", { className: nestedLabel ? 'pds-multi-select-nested-label-container' : '', children: [renderLabel(), displayChoices?.map((selection, index) => {
                    return (_jsxs("li", { className: "search-choice", children: [_jsx("span", { children: selection?.text }), _jsx("div", { id: "search-choice-close", className: "search-choice-close", ref: removeRef, role: "button", tabIndex: -1, onClick: (e) => {
                                    e.preventDefault();
                                    removeValue(index);
                                }, onKeyDown: (e) => {
                                    e.preventDefault();
                                } })] }, selection?.value));
                }), renderInput('')] }));
    };
    const renderResults = () => {
        if (!isOpen)
            return null;
        let previousGroupId = null;
        return (_jsxs("ul", { id: `multi-select-results-${uid}`, className: "pds-multi-select-results", role: "listbox", children: [limited.map((choice, index) => {
                    const { text: choiceText, value: choiceValue, group, ...rest } = choice;
                    const ref = index === highlightedIndex ? highlightedRef : null;
                    const id = `multi-select-result-${uid}-${choiceIds.get(choice)}`;
                    const className = classnames('pds-multi-select-result', {
                        'highlighted-result': index === highlightedIndex,
                        'group-option': !!groups,
                        'selected-result': selectedChoices.includes(choice),
                    });
                    // onMouseEnter is fired when scrolling, so we use onMouseMove to
                    // capture only events when the mouse position has changed.
                    const onMouseMove = () => setHighlightedIndex(index);
                    const onMouseDown = () => {
                        setTimeout(focus, 0);
                        choose(choiceValue);
                    };
                    let groupItem = null;
                    if (groups && group) {
                        // If the group has changed, include a group item.
                        if (group.name && group.id !== previousGroupId) {
                            groupItem = (_jsx("li", { className: "group-result", children: matches(group.name, pattern) }));
                        }
                        previousGroupId = group.id;
                    }
                    return (_jsxs(Fragment, { children: [groupItem, _jsx("li", { ref: ref, id: id, className: className, onMouseMove: onMouseMove, onMouseDown: onMouseDown, role: "option", "aria-selected": "false", ...rest, children: matches(choiceText, pattern) })] }, choiceValue));
                }), limit != null && filtered.length > limit && (_jsxs("li", { className: "pds-chosen-and-more", children: ["\u2026and ", filtered.length - limit, " more"] }))] }));
    };
    const className = classnames(classNameProp, 'pds-chosen pds-chosen-container', {
        'pds-chosen-open': isOpen,
        'pds-chosen-with-label': groupLabel && selectedChoices,
        'pds-chosen-select': select,
    });
    const activeId = choiceIds.get(limited[highlightedIndex]);
    const renderInput = (placeholderText = placeholder) => {
        if (disabled)
            return _jsx("input", { placeholder: placeholderText, disabled: true });
        const type = email ? 'email' : 'text';
        return (_jsx("input", { ref: inputRef, type: type, spellCheck: false, autoComplete: "off", value: query, onChange: onChangeQuery, onKeyDown: onKeyDown, onKeyUp: onKeyUp, className: "pds-multi-select-search-input", placeholder: placeholderText, role: "combobox", "aria-owns": `chosen-results-${uid}`, "aria-activedescendant": `chosen-result-${uid}-${activeId}`, "aria-controls": "multi-select-drop", "aria-expanded": "false" }));
    };
    const renderRequiredInput = () => {
        if (disabled || !required)
            return null;
        // Make sure at least one choice (existing or new) is selected
        const value = displayChoices ? displayChoices[0]?.value : '';
        return (_jsx("input", { ref: requiredInputRef, onChange: () => '', onFocus: focus, value: value, type: "text", required: !isOpen, tabIndex: -1, style: {
                // Ensure correct positioning of validation popup
                height: '100%',
                position: 'absolute',
                pointerEvents: 'none',
                opacity: 0,
            }, "aria-hidden": "true" }));
    };
    function renderValues() {
        if (name == null || !selectedValues) {
            return null;
        }
        else {
            return selectedValues.map((value, index) => {
                return (_jsx("input", { type: "hidden", name: name + '[]', multiple: true, value: value, disabled: disabled }, index));
            });
        }
    }
    function renderNewValues() {
        if (newName == null || !newValues) {
            return null;
        }
        else {
            return newValues.map((value, index) => {
                return (_jsx("input", { type: "hidden", name: newName + '[]', multiple: true, value: value, disabled: disabled }, index));
            });
        }
    }
    function renderLabel() {
        if (!nestedLabel) {
            return null;
        }
        else {
            return _jsx("label", { className: "pds-label", children: nestedLabel });
        }
    }
    function renderChoices() {
        const className = classnames('pds-button pds-button-chosen pds-multi-select-choices', {
            'pds-button-sm': select && small,
            open: isOpen,
            'pds-multi-select-nested-label': nestedLabel,
        });
        return (_jsx(Button, { id: id, ref: buttonRef, disabled: disabled, className: className, onKeyDownCapture: onButtonKeyDown, onMouseDown: onButtonMouseDown, onClick: onButtonClick, ...rest, children: selectedChoices.length > 0 || newChoices.length > 0 ? (renderSelections()) : (_jsxs(_Fragment, { children: [renderLabel(), _jsx("li", { className: "search-field", children: renderInput() })] })) }));
    }
    function renderNoResults() {
        return (_jsxs("div", { className: "pds-chosen-no-results", children: ["No results match ", query] }));
    }
    function renderBackdrop() {
        if (!isOpen)
            return null;
        return (_jsx("div", { className: "multi-select-menu-backdrop", role: "none", onClick: close }));
    }
    function renderDropdownMenu() {
        if (!isOpen)
            return null;
        return (_jsx("div", { className: "pds-multi-select-drop", children: _jsx("div", { id: "multi-select-drop", className: "pds-multi-select-dropdown", children: limited.length ? renderResults() : renderNoResults() }) }));
    }
    return (_jsxs(Fragment, { children: [renderBackdrop(), renderValues(), renderNewValues(), _jsxs("div", { className: className, children: [renderRequiredInput(), renderChoices(), renderDropdownMenu()] })] }));
});
